import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    applyStaticGeometryRowMeasurements,
    type StaticGeometryRowMeasurement,
    type StaticGeometryRowMeasurementSample,
    type StaticGeometryRowModel,
} from 'lib/staticGeometryRowMeasurements'

const SCROLL_QUIET_MS = 250
// usePanelVirtualizer retains active-scroll geometry for 2s. Starting output correction
// after that latch releases avoids publishing passive resizes that can only enter its retry queue.
const POST_SCROLL_MEASUREMENT_DELAY_MS = 2000
const IDLE_TIMEOUT_MS = 1000
const MAX_ROWS_PER_IDLE_SLICE = 8

type IdleDeadlineLike = {
    didTimeout: boolean
    timeRemaining: () => number
}

type MeasurementClient = {
    enabled: boolean
    rowsByKey: Map<string, StaticGeometryRowModel>
    rowNodes: Map<string, HTMLElement>
    measureRow: (row: HTMLElement) => StaticGeometryRowMeasurementSample | null
    commit: (updates: Map<string, StaticGeometryRowMeasurement>) => void
}

type MeasurementCoordinator = {
    addClient: (client: MeasurementClient) => void
    removeClient: (client: MeasurementClient) => boolean
    schedule: (delay?: number) => void
}

const coordinatorsByScrollElement = new WeakMap<HTMLElement, MeasurementCoordinator>()

function createMeasurementCoordinator(scrollElement: HTMLElement): MeasurementCoordinator {
    const clients = new Set<MeasurementClient>()
    const observedTargets = new Set<Element>()
    let resizeObserver: ResizeObserver | null = null
    let scrollQuietTimer: number | null = null
    let idleHandle: number | null = null
    let idleHandleUsesRequestIdleCallback = false
    let scrollGeneration = 0
    let isScrolling = false
    let rerunRequested = false

    const cancelIdleWork = () => {
        if (idleHandle == null) return

        if (idleHandleUsesRequestIdleCallback && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(idleHandle)
        } else {
            window.clearTimeout(idleHandle)
        }
        idleHandle = null
    }

    const disconnectObservation = () => {
        resizeObserver?.disconnect()
        observedTargets.clear()
    }

    const cancelScheduledWork = () => {
        if (scrollQuietTimer != null) {
            window.clearTimeout(scrollQuietTimer)
            scrollQuietTimer = null
        }
        cancelIdleWork()
    }

    const requestIdleWork = (callback: (deadline: IdleDeadlineLike) => void) => {
        if (typeof window.requestIdleCallback === 'function') {
            idleHandleUsesRequestIdleCallback = true
            idleHandle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS })
            return
        }

        idleHandleUsesRequestIdleCallback = false
        idleHandle = window.setTimeout(
            () => callback({ didTimeout: true, timeRemaining: () => 0 }),
            0,
        )
    }

    const schedule = (delay = SCROLL_QUIET_MS) => {
        if (isScrolling || clients.size === 0) return
        if (scrollQuietTimer != null) return
        if (idleHandle != null) {
            rerunRequested = true
            return
        }

        scrollQuietTimer = window.setTimeout(() => {
            scrollQuietTimer = null
            runMeasurementPass()
        }, delay)
    }

    const syncObservationTargets = (nextTargets: Set<Element>) => {
        if (typeof ResizeObserver === 'undefined') return

        if (!resizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                if (!isScrolling) schedule()
            })
        }

        observedTargets.forEach(target => {
            if (!nextTargets.has(target)) resizeObserver?.unobserve(target)
        })
        nextTargets.forEach(target => {
            if (!observedTargets.has(target)) resizeObserver?.observe(target)
        })
        observedTargets.clear()
        nextTargets.forEach(target => observedTargets.add(target))
    }

    const runMeasurementPass = () => {
        if (isScrolling || clients.size === 0 || idleHandle != null) return

        const work = Array.from(clients).flatMap(client =>
            Array.from(client.rowNodes.entries()).map(([key, node]) => ({ client, key, node })),
        )
        const updatesByClient = new Map<
            MeasurementClient,
            Map<string, StaticGeometryRowMeasurement>
        >()
        const observationTargets = new Set<Element>()
        let nextIndex = 0

        const processSlice = (deadline: IdleDeadlineLike) => {
            idleHandle = null
            if (isScrolling || clients.size === 0) return

            let processed = 0
            while (
                nextIndex < work.length &&
                processed < MAX_ROWS_PER_IDLE_SLICE &&
                (processed === 0 || deadline.didTimeout || deadline.timeRemaining() > 1)
            ) {
                const { client, key, node } = work[nextIndex++]
                processed += 1
                if (!clients.has(client) || !client.enabled) continue

                if (!node.isConnected) {
                    if (client.rowNodes.get(key) === node) client.rowNodes.delete(key)
                    continue
                }

                const row = client.rowsByKey.get(key)
                if (!row?.staticSignature) continue

                const sample = client.measureRow(node)
                if (!sample || !Number.isFinite(sample.height) || sample.height <= 0) continue

                let updates = updatesByClient.get(client)
                if (!updates) {
                    updates = new Map()
                    updatesByClient.set(client, updates)
                }
                updates.set(key, { height: sample.height, signature: row.staticSignature })
                sample.observationTargets.forEach(target => {
                    if (target.isConnected) observationTargets.add(target)
                })
            }

            if (nextIndex < work.length) {
                requestIdleWork(processSlice)
                return
            }

            if (isScrolling || clients.size === 0) return

            syncObservationTargets(observationTargets)
            updatesByClient.forEach((updates, client) => {
                if (clients.has(client)) client.commit(updates)
            })
            if (rerunRequested) {
                rerunRequested = false
                schedule()
            }
        }

        requestIdleWork(processSlice)
    }

    const finishScroll = () => {
        isScrolling = false
        if (scrollQuietTimer != null) {
            window.clearTimeout(scrollQuietTimer)
            scrollQuietTimer = null
        }
        schedule(POST_SCROLL_MEASUREMENT_DELAY_MS)
    }

    const checkScrollSettled = (generation: number) => {
        scrollQuietTimer = window.setTimeout(() => {
            scrollQuietTimer = null
            if (generation !== scrollGeneration) {
                checkScrollSettled(scrollGeneration)
                return
            }

            finishScroll()
        }, SCROLL_QUIET_MS)
    }

    const handleScroll = () => {
        scrollGeneration += 1
        if (!isScrolling) {
            isScrolling = true
            rerunRequested = false
            cancelScheduledWork()
            disconnectObservation()
        }
        if (scrollQuietTimer == null) checkScrollSettled(scrollGeneration)
    }

    const destroy = () => {
        scrollElement.removeEventListener('scroll', handleScroll)
        scrollElement.removeEventListener('scrollend', finishScroll)
        cancelScheduledWork()
        disconnectObservation()
        resizeObserver = null
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })
    scrollElement.addEventListener('scrollend', finishScroll, { passive: true })

    return {
        addClient(client) {
            clients.add(client)
            schedule(0)
        },
        removeClient(client) {
            clients.delete(client)
            if (clients.size > 0) {
                schedule(0)
                return false
            }

            destroy()
            return true
        },
        schedule,
    }
}

function acquireMeasurementCoordinator(
    scrollElement: HTMLElement,
    client: MeasurementClient,
): () => void {
    let coordinator = coordinatorsByScrollElement.get(scrollElement)
    if (!coordinator) {
        coordinator = createMeasurementCoordinator(scrollElement)
        coordinatorsByScrollElement.set(scrollElement, coordinator)
    }
    coordinator.addClient(client)

    return () => {
        if (coordinator?.removeClient(client)) {
            coordinatorsByScrollElement.delete(scrollElement)
        }
    }
}

type UseStaticGeometryRowMeasurementsOptions<T extends StaticGeometryRowModel> = {
    enabled: boolean
    rows: T[]
    resolveScrollElement: () => HTMLElement | null
    measureRow: (row: HTMLElement) => StaticGeometryRowMeasurementSample | null
}

export function useStaticGeometryRowMeasurements<T extends StaticGeometryRowModel>({
    enabled,
    rows,
    resolveScrollElement,
    measureRow,
}: UseStaticGeometryRowMeasurementsOptions<T>) {
    const [measurements, setMeasurements] = useState<Map<string, StaticGeometryRowMeasurement>>(
        () => new Map(),
    )
    const coordinatorRef = useRef<MeasurementCoordinator | null>(null)
    const clientRef = useRef<MeasurementClient>({
        enabled,
        rowsByKey: new Map(),
        rowNodes: new Map(),
        measureRow,
        commit: () => undefined,
    })
    const client = clientRef.current
    client.enabled = enabled
    client.rowsByKey = new Map(rows.map(row => [String(row.key), row]))
    client.measureRow = measureRow
    client.commit = updates => {
        setMeasurements(current => {
            let changed = false
            const next = new Map(current)
            updates.forEach((measurement, key) => {
                const previous = current.get(key)
                if (
                    previous?.signature === measurement.signature &&
                    previous.height === measurement.height
                ) {
                    return
                }

                next.set(key, measurement)
                changed = true
            })
            return changed ? next : current
        })
    }

    useEffect(() => {
        if (!enabled) {
            client.rowNodes.clear()
            setMeasurements(current => (current.size === 0 ? current : new Map()))
            return
        }

        const scrollElement = resolveScrollElement()
        if (!scrollElement) return

        const release = acquireMeasurementCoordinator(scrollElement, client)
        coordinatorRef.current = coordinatorsByScrollElement.get(scrollElement) ?? null

        return () => {
            coordinatorRef.current = null
            release()
        }
    }, [client, enabled, resolveScrollElement])

    useEffect(() => {
        if (enabled) coordinatorRef.current?.schedule()
    }, [enabled, rows])

    const registerRow = useCallback((node: HTMLElement | null, key: unknown) => {
        if (!node) return

        const normalizedKey = String(key)
        if (clientRef.current.rowNodes.get(normalizedKey) === node) {
            coordinatorRef.current?.schedule()
            return
        }

        clientRef.current.rowNodes.set(normalizedKey, node)
        coordinatorRef.current?.schedule()
    }, [])

    const measuredRows = useMemo(
        () => (enabled ? applyStaticGeometryRowMeasurements(rows, measurements) : rows),
        [enabled, measurements, rows],
    )

    return { registerRow, rows: measuredRows }
}
